<script lang="ts">
    import { onMount } from 'svelte';
    import { getPathParams } from 'svelte-mini-router';
    import { translate } from '@/utils/format';
    import { goTo } from '@/utils/router';
    import { Spinner } from '@/components';
    import { initSsoSession, setSsoSession } from '@/services/sso';

    let loading = $state(true);
    let partnerName = $state('');
    let logoUrl = $state('');
    let errorBanner = $state('');

    function getQueryParam(name: string): string | null {
        try {
            return new URL(window.location.href).searchParams.get(name);
        } catch {
            return null;
        }
    }

    onMount(async () => {
        const params = getPathParams();
        const partnerId = String(params?.['partnerId'] || '').trim();
        const code = getQueryParam('code') || getQueryParam('CODE');

        if (!partnerId) {
            loading = false;
            errorBanner = translate('SSO_CODE_EXPIRED', 'El enlace ha caducado. Puedes continuar igualmente.');
            setTimeout(() => goTo('/'), 2500);
            return;
        }

        const session = await initSsoSession(partnerId, code);

        if (session) {
            partnerName = session.branding.partnerName;
            logoUrl = session.branding.logoUrl;
        } else {
            // No code or exchange failed: proceed without SSO (fallback path)
            errorBanner = translate('SSO_CODE_EXPIRED', 'El enlace ha caducado. Puedes continuar igualmente.');
            // Still store a minimal session so downstream pages have partnerId
            setSsoSession({
                partnerId,
                webToken: '',
                prefill: {},
                branding: { logoUrl: '', primaryColor: '', partnerName: partnerId },
                partnerClaims: { partnerId },
            });
            partnerName = partnerId;
        }

        loading = false;

        // Brief splash → onboarding
        setTimeout(() => goTo(`/${partnerId}/onboarding`), 1400);
    });
</script>

<section class="ssoStart">
    {#if loading}
        <div class="ssoStart__splash">
            <Spinner text="" />
            <p class="ssoStart__loadingText">
                {translate('SSO_SPLASH_LOADING', 'Preparando tu perfil...')}
            </p>
        </div>
    {:else}
        <div class="ssoStart__splash">
            {#if logoUrl}
                <img class="ssoStart__logo" src={logoUrl} alt={partnerName} />
            {:else}
                <div class="ssoStart__logoFallback">{partnerName.charAt(0).toUpperCase()}</div>
            {/if}

            <h1 class="ssoStart__title">
                {translate('SSO_SPLASH_WELCOME', 'Bienvenido a {partner}', { partner: partnerName })}
            </h1>
            <p class="ssoStart__subtitle">
                {translate('SSO_SPLASH_SUBTITLE', 'Vamos a crear tu perfil nutricional personalizado')}
            </p>

            {#if errorBanner}
                <p class="ssoStart__warn">{errorBanner}</p>
            {/if}

            <p class="ssoStart__poweredBy">
                {translate('SSO_POWERED_BY', 'powered by Caloo')}
            </p>
        </div>
    {/if}
</section>

<style lang="scss">
    .ssoStart {
        min-height: 100vh;
        background: var(--backgroundMain);
        display: grid;
        place-items: center;
        padding: 24px 20px;
        font-family: 'Montserrat', sans-serif;
    }

    .ssoStart__splash {
        display: grid;
        gap: 16px;
        justify-items: center;
        text-align: center;
        max-width: 380px;
        width: 100%;
    }

    .ssoStart__logo {
        width: 80px;
        height: 80px;
        border-radius: 20px;
        object-fit: cover;
        border: 1px solid var(--border);
    }

    .ssoStart__logoFallback {
        width: 80px;
        height: 80px;
        border-radius: 20px;
        background: var(--partner-primary, var(--primary));
        color: #12121f;
        display: grid;
        place-items: center;
        font-size: 32px;
        font-weight: 900;
    }

    .ssoStart__title {
        margin: 0;
        font-size: 24px;
        font-weight: 900;
        color: var(--black);
        letter-spacing: -0.02em;
        line-height: 1.2;
    }

    .ssoStart__subtitle {
        margin: 0;
        font-size: 14px;
        color: var(--subtleText);
        line-height: 1.45;
    }

    .ssoStart__loadingText {
        margin: 8px 0 0;
        font-size: 14px;
        color: var(--subtleText);
    }

    .ssoStart__warn {
        margin: 0;
        font-size: 13px;
        color: var(--subtleText);
        background: var(--backgroundMain);
        border: 1px solid var(--border);
        border-radius: 12px;
        padding: 10px 14px;
        line-height: 1.4;
    }

    .ssoStart__poweredBy {
        margin: 8px 0 0;
        font-size: 11px;
        color: var(--subtleText);
        opacity: 0.6;
        letter-spacing: 0.04em;
    }
</style>
